import { AppData, Lightning, Registry } from '@lightningjs/sdk';
import { List } from '@lightningjs/ui';
import { STANDARD_TRANSLATION } from 'support/animations';
import PortraitTile from 'components/common/tiles/PortraitTile';
import ChannelTile from 'components/common/tiles/ChannelTile';
import VideoTile from 'components/common/tiles/VideoTile';
import RecommendedVideoTile from 'components/common/tiles/RecommendedVideoTile';
import { ExpandableList } from 'components/common/ExpandableList';
import FeaturedTile, {
  FeaturedTileTemplateSpec,
} from 'components/common/tiles/FeaturedTile';
import { getFontFaceFromStyle } from 'support/textUtils';
import Highlight from 'components/common/Highlight';
import LandscapeTile from 'components/common/tiles/LandscapeTile';
import SquareContentHubTile from 'components/common/tiles/SquareContentHubTile';
import VerticalContentHubTile from 'components/common/tiles/VerticalContentHubTile';
import HorizontalContentHubTile from 'components/common/tiles/HorizontalContentHubTile';
import ExpandingDetailTile from 'components/common/tiles/ExpandingDetailTile';
import { constants } from 'aliases';
import LiveEventTile from 'components/common/tiles/LiveEventTile';
import SearchResultTile from 'components/common/tiles/SearchResultTile';
import { LaneItem } from 'services/cwData';
import { ImageKey, PlayableMediaImages } from 'types/api/media';
import Tile, {
  CustomTileSpec,
  TileParentContext,
  TileTemplateSpec,
} from 'components/common/tiles/Tile';
import { Merge } from 'types/index';
import AbstractCarousel from 'components/common/carousels/AbstractCarousel';
import Boundary from 'components/common/carousels/Boundary';
import FavoriteChannelTile from '../tiles/FavoriteChannelTile';
import { IndexData } from 'types/lightning';
import AbstractContinueTile from 'components/common/tiles/AbstractContinueTile';
import VerticalFeaturedTile from '../tiles/VerticalFeaturedTile';
import HorizontalFeaturedTile from '../tiles/HorizontalFeaturedTile';
import BezelContentHubTile from '../tiles/BezelContentHubTile';
import TextLabel from '../TextLabel';
import {
  DirectionsType,
  isHorizontalDirection,
  isVerticalDirection,
} from 'support/keyEventUtils';

export type TileListTypes =
  | typeof ChannelTile
  | typeof AbstractContinueTile
  | typeof FeaturedTile
  | typeof LandscapeTile
  | typeof PortraitTile
  | typeof SearchResultTile
  | typeof VideoTile
  | typeof RecommendedVideoTile
  | typeof SquareContentHubTile
  | typeof VerticalContentHubTile
  | typeof HorizontalContentHubTile
  | typeof LiveEventTile
  | typeof ExpandingDetailTile
  | typeof FavoriteChannelTile
  | typeof BezelContentHubTile;

export type CarouselLaneItem = LaneItem | CustomTileSpec;

export interface CarouselTemplateSpec extends Lightning.Component.TemplateSpec {
  label?: string;
  listType?: TileListTypes;
  labelProps: any;
  itemWidth: number;
  expandedItemWidth: number | null;
  imageKey: ImageKey | null;
  expandedImageKey: keyof PlayableMediaImages | null;
  laneItems: CarouselLaneItem[];
  laneIndex: number;
  enableRequests: boolean;
  navBarOffset: number | null;
  isNavBarVisible?: boolean;
  scrollTransition: object;
  rowListOffset: number;

  Content: {
    Label: typeof TextLabel;
    ListContainer: {
      List: typeof List;
      Highlight: typeof Highlight;
      BoundaryLeft: typeof Boundary;
      BoundaryRight: typeof Boundary;
    };
  };
}

const BORDER_GAP = 30;

const FEATURED_BOUNDARY_WIDTH = 60; // Avoid hover conflicts with side nav
const BOUNDARY_WIDTH = 100;
const SCROLL_INITIAL_INTERVAL = 500;
const SCROLL_REDUCE_INTERVAL = 100;
const SCROLL_MIN_INTERVAL = 200;

const HIGHLIGHT_SIZE_OFFSET = constants.ui.highlightSizeOffset;
const HIGHLIGHT_POSITION_OFFSET = constants.ui.highlightPositionOffset;

export const ROW_LIST_OFFSET_Y = 62;

export const isFeaturedListType = (listType?: TileListTypes) => {
  return (
    listType === FeaturedTile ||
    listType === VerticalFeaturedTile ||
    listType === HorizontalFeaturedTile
  );
};
export default abstract class Carousel<
  TemplateSpec extends CarouselTemplateSpec = CarouselTemplateSpec,
> extends AbstractCarousel<TemplateSpec> {
  protected _Content = (this as Carousel).getByRef('Content')!;
  protected _Label = this._Content.getByRef('Label')!;
  protected _ListContainer = this._Content.getByRef('ListContainer')!;
  protected _List = this._ListContainer.getByRef('List')!;
  protected _Highlight = this._ListContainer.getByRef('Highlight')!;
  protected _BoundaryLeft = this._ListContainer.getByRef('BoundaryLeft')!;
  protected _BoundaryRight = this._ListContainer.getByRef('BoundaryRight')!;

  protected _label: TemplateSpec['label'] = undefined;
  protected _laneItems: TemplateSpec['laneItems'] = [];
  protected _laneIndex: TemplateSpec['laneIndex'] = 0;
  // Setters defined by children
  protected _imageKey: TemplateSpec['imageKey'] = null;
  protected _expandedImageKey: TemplateSpec['expandedImageKey'] = null;

  itemWidth: TemplateSpec['itemWidth'] = 0;
  expandedItemWidth: TemplateSpec['expandedItemWidth'] = null;

  // Internal properties
  private _onRepositionedCallback: (() => void) | undefined;
  private _shouldScroll = false;
  private _scrollIntervalId = null;
  private _scrollInterval = SCROLL_INITIAL_INTERVAL;
  private _adjustIndex = -1 | 1;
  private _listType: TemplateSpec['listType'] = undefined;
  private _navBarOffset: TemplateSpec['navBarOffset'] = null;
  private _isNavBarVisible = true;
  protected _rowListOffset = ROW_LIST_OFFSET_Y;

  get title() {
    return this._Label.text?.text ?? '';
  }

  set label(label: TemplateSpec['label']) {
    this._label = label;
    this.updateLabel();
    this.updateCarousel();
  }

  get label() {
    return this._label;
  }

  get rowListOffset() {
    return this._rowListOffset;
  }

  set rowListOffset(value: number) {
    this._rowListOffset = value;

    this.updateLabel();
  }

  set labelProps(props: Lightning.textures.TextTexture) {
    this._Label.patch(props);
  }

  set laneIndex(laneIndex: TemplateSpec['laneIndex']) {
    this._laneIndex = laneIndex;
  }

  get laneIndex() {
    return this._laneIndex;
  }

  get listType() {
    return this._listType;
  }

  set listType(type: TemplateSpec['listType']) {
    this._listType = type;

    if (!type) return;

    this.updateFocusStyling({
      w: type.highlightWidth + HIGHLIGHT_SIZE_OFFSET,
      h: type.highlightHeight + HIGHLIGHT_SIZE_OFFSET,
      x: HIGHLIGHT_POSITION_OFFSET,
      y: HIGHLIGHT_POSITION_OFFSET,
    });

    this._List.patch({
      w: type.width,
      h: type.height,
      itemType: type,
    });

    this.updateHoverBoundary();
  }

  get imageKey() {
    return this._imageKey;
  }

  get expandedImageKey() {
    return this._expandedImageKey;
  }

  get laneItems() {
    return this._laneItems;
  }

  set laneItems(laneItems: TemplateSpec['laneItems']) {
    this._laneItems = laneItems;
    this.updateCarousel();
  }

  get itemWrappers() {
    return this._List.itemWrappers;
  }

  set enableRequests(value: TemplateSpec['enableRequests']) {
    this._List.enableRequests = value;
  }

  get length() {
    return this._List.length;
  }

  set index(index: number) {
    this._List.setIndex(index);
  }

  get index() {
    return this._List.index;
  }

  set navBarOffset(value: TemplateSpec['navBarOffset']) {
    this._navBarOffset = value;

    this.updateHoverBoundary();
  }

  get navBarOffset() {
    return this._navBarOffset ?? 0;
  }

  set isNavBarVisible(value: boolean) {
    this._isNavBarVisible = value;
    this.updateHoverBoundary();
  }

  get isNavBarVisible() {
    return this._isNavBarVisible;
  }

  set scrollTransition(value: object) {
    this._List.patch({ scrollTransition: value });
  }

  static get listSignals() {
    return {
      onIndexChanged: '_onIndexChanged',
      onItemsRepositioned: '_onItemsRepositioned',
    };
  }

  static override _template(): Lightning.Component.Template<CarouselTemplateSpec> {
    return {
      Content: {
        Label: {
          type: TextLabel,
          visible: true,
          text: {
            verticalAlign: 'middle',
            lineHeight: 42,
            fontFace: getFontFaceFromStyle('medium'),
            fontSize: 36,
          },
        },
        ListContainer: {
          List: {
            type: ExpandableList,
            direction: 'row',
            spacing: BORDER_GAP,
            scrollTransition: { ...STANDARD_TRANSLATION.x },
            signals: {
              ...this.listSignals,
            },
          },
          Highlight: {
            type: Highlight,
            visible: false,
            zIndex: constants.ui.zIndex.floatingLayer - 1,
          },
          BoundaryLeft: {
            type: Boundary,
            visible: true,
            zIndex: constants.ui.zIndex.floatingLayer,
          },
          BoundaryRight: {
            type: Boundary,
            visible: true,
            zIndex: constants.ui.zIndex.floatingLayer,
          },
        },
      },
    };
  }

  private onGlobalHoverStateChanged(isHovered: boolean) {
    isHovered ? this.onGlobalHoverEnabled() : this.onGlobalHoverDisabled();
  }

  override _setup() {
    this._List.scroll = (
      itemWrapper: Lightning.Element,
      indexData: IndexData,
    ) => {
      // Positions carousel with the intent of left fixed focus
      return 0 - (this.getItemWidth() + this.getBorderGap()) * indexData.index;
    };

    this._Highlight.transition('w').on('finish', () => {
      this._List.reposition(0);
    });
    this._Highlight.transition('w').on('progress', () => {
      this._List.reposition(0);
    });

    this.updateHoverBoundary();
  }

  override _getFocused() {
    return this._List;
  }

  override _focus() {
    this.showFocusBorder(true);
  }

  override _unfocus() {
    this.showFocusBorder(false);
  }

  override _active() {
    super._active();

    AppData?.device.addHoverStateChangedAction(
      this.id.toString(),
      this.onGlobalHoverStateChanged.bind(this),
    );
  }

  override _inactive() {
    super._inactive();

    AppData?.device.removeHoverStateChangedAction(this.id.toString());
    this.clearScrollInterval();
  }

  override onSwipeAction = (direction: DirectionsType, strength: number) => {
    if (!this.hovered) return;
    if (isVerticalDirection(direction)) return;

    const newIndex = Math.max(
      0,
      this.index + strength * (direction === 'Left' ? -1 : 1),
    );
    this.index = newIndex;
  };

  override getItemWidth(): number {
    return this.itemWidth;
  }

  override getBorderGap(): number {
    return BORDER_GAP;
  }

  private updateLabel() {
    const label = this._label;
    if (label) {
      this._Label.patch({ visible: true, text: { text: label } });
      this._ListContainer.patch({ y: this.rowListOffset });
    } else {
      this._Label.patch({ visible: false });
      this._ListContainer.patch({ y: 0 });
    }
  }

  protected updateCarousel() {
    this._List.clear();
    const laneItems = this._laneItems;
    if (!laneItems.length) return;

    const items = this.buildTiles(laneItems);
    this._List.patch({ items });

    this.updateHoverBoundary();
  }

  protected buildTiles(laneItems: CarouselLaneItem[]) {
    return laneItems.map((item, index) => {
      if (this.isCustomTile(item)) {
        return {
          ...item,
          isItemHover: true,
          hoverHighlightEnabled: this.useHighlightFocus(),
        };
      }

      return this.buildTile(item as LaneItem, index);
    });
  }

  protected buildTile(item: LaneItem, index: number) {
    const expandedImageKey = this._expandedImageKey;
    const laneIndex = this._laneIndex;
    const label = this._label;

    const parentContext: TileParentContext = {
      crossIndex: index,
      mainIndex: laneIndex,
      rowTitle: label ?? '',
      // Default to false if null
      isTivoSource: !!item.isTivoItem,
      isContinueSource: !!item.isContinueItem,
    };

    const result: Lightning.Element.PatchTemplate<
      Merge<TileTemplateSpec, FeaturedTileTemplateSpec>
    > = {
      data: item,
      notifyWhenFullyVisible: true,
      parentContext,
      isItemHover: true,
      hoverHighlightEnabled: this.useHighlightFocus(),
    };

    result.imageUrl = this.getImageUrl(item);

    if (item.images && expandedImageKey && expandedImageKey in item.images)
      result.expandedImageUrl =
        item.images[expandedImageKey as keyof typeof item.images];

    return result;
  }

  protected getImageUrl(item: LaneItem) {
    const imageKey = this._imageKey;
    if (item.images && imageKey && imageKey in item.images)
      return item.images[imageKey as keyof typeof item.images];

    return undefined;
  }

  private _onIndexChanged(args: {
    previousIndex: number;
    index: number;
    dataLength: number;
  }) {
    this.checkBoundaryVisibility(args.index, args.dataLength);
    this.fireAncestors('$onCarouselIndexChange', args);
  }

  private checkBoundaryVisibility(index: number, count: number) {
    this._BoundaryLeft.patch({ visible: index !== 0 });

    let showRightBoundary = true;
    const remainingCount = count - index;
    if (remainingCount > 0) {
      const availableSpace = 1920 - this.navBarOffset!;
      const baseItemWidth = this.itemWidth + this.getBorderGap();
      // Handles cases the focused item could be a different size
      const remainingItemsWidth =
        this.getFirstItemMaxWidth() + (remainingCount - 1) * baseItemWidth;

      if (remainingItemsWidth < availableSpace) {
        showRightBoundary = false;
      }
    }
    this._BoundaryRight.patch({ visible: showRightBoundary });
  }

  private _onItemsRepositioned() {
    if (this._onRepositionedCallback) this._onRepositionedCallback();
    this._onRepositionedCallback = undefined;
  }

  override getTotalItemsWidth() {
    this.totalItemsWidth =
      (this.itemWidth + BORDER_GAP) * this._List.items.length;
    return this.totalItemsWidth;
  }

  override scrollUpdate(scrollTo: number, isHoverAction = false): void {
    const highlightX = scrollTo + HIGHLIGHT_POSITION_OFFSET;
    this.updateFocusStyling({ x: highlightX }, isHoverAction ? false : true);
  }

  $repositionList(onRepositionedCallback?: () => void) {
    this._onRepositionedCallback = onRepositionedCallback;
    this._List.reposition(0);
  }

  private boundaryHoverHandler(adjustIndex: -1 | 1) {
    this._scrollInterval = SCROLL_INITIAL_INTERVAL;
    this._adjustIndex = adjustIndex;
    this.handleHoverScrolling();
    this.resetScrollTimer();
  }

  protected boundaryUnhoverHandler() {
    this.clearScrollInterval();
  }

  private handleHoverScrolling() {
    // Make sure tiles consider themselves as hoverable
    if (!(this._List.currentItem as Tile).isHoverable) return;

    const newIndex: number = this.index + this._adjustIndex;
    if (newIndex < 0 || newIndex >= this._List.length) {
      // clear the scroll interval when coming to the edge of the list
      this.clearScrollInterval();
      return;
    }

    // scrolling by updating the index, similar behavior of pressing Left/Right key
    this.index = newIndex;
  }

  private scrollTimeoutFunction() {
    if (!this._shouldScroll) return;

    this.handleHoverScrolling();

    // update scrolling interval till SCROLL_MIN_INTERVAL
    if (this._scrollInterval > SCROLL_MIN_INTERVAL) {
      this._scrollInterval -= SCROLL_REDUCE_INTERVAL;
      this.resetScrollTimer();
    }
  }

  private resetScrollTimer() {
    this.clearScrollInterval();

    this._scrollIntervalId = Registry.setInterval(
      this.scrollTimeoutFunction.bind(this),
      this._scrollInterval,
    );

    this._shouldScroll = true;
  }

  private clearScrollInterval() {
    this._shouldScroll = false;

    if (this._scrollIntervalId !== null) {
      Registry.clearInterval(this._scrollIntervalId);
      this._scrollIntervalId = null;
    }
  }

  private showFocusBorder(state: boolean) {
    const visible = !AppData?.device.getIsHovering() && state;

    this.updateFocusStyling({ visible });
  }

  protected updateFocusStyling(transition: object, isSmooth = false) {
    if (!this.useHighlightFocus()) return;

    const style = isSmooth ? { smooth: { ...transition } } : transition;

    this._Highlight.patch(style);
  }

  protected onGlobalHoverEnabled() {
    this.showFocusBorder(false);
  }

  protected onGlobalHoverDisabled() {
    // If this carousel is the regularly focused / centered carousel, restore focus border to it
    this.showFocusBorder(!!this.hasFocus());
  }

  private updateHoverBoundary() {
    this.updateHoverBoundarySize();
    this.updateHoverBoundaryPositions();

    this.checkBoundaryVisibility(this.index, this.length);
  }

  private updateHoverBoundarySize() {
    // Add margin to extend the rendering area to the bottom of the row, so that
    // it still allows hover when top portions are out of view.
    this.boundsMargin = [0, 0, 0, this._List.h];

    const boundaryProperties = {
      w: this.getHoverBoundaryWidth(),
      h: this.listType?.height,
      boundaryHoverHandler: this.boundaryHoverHandler.bind(this),
      boundaryUnhoverHandler: this.boundaryUnhoverHandler.bind(this),
    };

    this._BoundaryLeft.patch({
      ...boundaryProperties,
      visible: false,
    });
    // TODO: Consider hiding this boundary when all items fit on the screen for better hovering experience
    this._BoundaryRight.patch({
      ...boundaryProperties,
    });
  }

  private updateHoverBoundaryPositions() {
    this._BoundaryLeft.patch({
      x: this.isNavBarVisible ? 0 : 0 - this.navBarOffset!, // Mainly for avoiding nav bar conflicts
    });

    this._BoundaryRight.patch({
      x: 1920 - this.navBarOffset! - this.getHoverBoundaryWidth(),
    });
  }

  private getHoverBoundaryWidth() {
    return isFeaturedListType(this.listType)
      ? FEATURED_BOUNDARY_WIDTH
      : BOUNDARY_WIDTH;
  }

  private isCustomTile(item: CarouselLaneItem) {
    return (item as CustomTileSpec).isCustomTile;
  }
}
